/**
 * Resolve item/variation addon group refs to actual addon items using API addongroups.
 * API: item.addon = [{ addon_group_id: "1823975", ... }]; details.addongroups = [{ addongroupid, addongroup_name, addongroupitems: [{ addonitemid, addonitem_name, addonitem_price, item_price, attributes }] }].
 * Returns array in shape AddQuantityModal expects: addons_name, addons_price, addons_discount_price, addonitemid, addongroupid, addongroup_name, addons_food_type.
 */
const resolveAddonGroupsToItems = (addonGroups, addongroupsFromApi) => {
  if (!addonGroups || !Array.isArray(addonGroups) || addonGroups.length === 0) return [];
  if (!addongroupsFromApi || !Array.isArray(addongroupsFromApi)) return [];

  const addOns = [];
  const groupIdsSeen = new Set();

  addonGroups.forEach((groupRef) => {
    const groupId = String(groupRef.addon_group_id ?? groupRef.addongroupid ?? '').trim();
    if (!groupId || groupIdsSeen.has(groupId)) return;
    groupIdsSeen.add(groupId);

    const group = addongroupsFromApi.find(
      (g) => String(g.addongroupid ?? g.addon_group_id ?? '').trim() === groupId
    );
    if (!group || !group.addongroupitems || !Array.isArray(group.addongroupitems)) return;

    const groupIdVal = String(group.addongroupid ?? group.addon_group_id ?? groupId);
    const groupName = String(group.addongroup_name ?? group.addon_groupname ?? '').trim();

    group.addongroupitems.forEach((addonItem) => {
      const price = parseFloat(addonItem.addonitem_price ?? addonItem.item_price ?? 0) || 0;
      const addonItemId = String(addonItem.addonitemid ?? addonItem.addon_item_id ?? '');
      const name = String(addonItem.addonitem_name ?? addonItem.addonitemname ?? addonItem.addon_name ?? '').trim();
      const attr = String(addonItem.attributes ?? '').trim();
      const foodType = attr === '1' ? 'veg' : attr === '2' ? 'non-veg' : 'veg';

      addOns.push({
        addons_name: name || 'Add-on',
        addons_price: Number(price).toFixed(2),
        addons_discount_price: Number(price).toFixed(2),
        addons_food_type: foodType,
        addonitemid: addonItemId,
        addongroupid: groupIdVal,
        addongroup_name: groupName,
        addon_group_id: groupIdVal,
        is_required: false
      });
    });
  });

  return addOns;
};

/** @deprecated Use resolveAddonGroupsToItems when addongroups are available. Fallback placeholder add-ons. */
const createPetpoojaAddOns = (addonGroups) => {
  const addOns = [];
  addonGroups.forEach((group) => {
    const groupId = group.addon_group_id;
    const maxSelection = group.addon_item_selection_max || 1;
    for (let i = 0; i < maxSelection; i++) {
      addOns.push({
        addons_name: `Add-on Option ${i + 1}`,
        addons_price: "15.00",
        addons_discount_price: "12.00",
        addons_food_type: "veg",
        addon_group_id: groupId,
        is_required: false
      });
    }
  });
  return addOns;
};

// Transform Petpooja menu data to match your existing menu structure
export const transformPetpoojaMenu = (petpoojaData, restaurantIdFromDetail = null) => {
  if (!petpoojaData || !petpoojaData.items || !Array.isArray(petpoojaData.items)) {
    return [];
  }

  const addongroupsFromApi = petpoojaData.addongroups && Array.isArray(petpoojaData.addongroups) ? petpoojaData.addongroups : [];

  const getAddonsForGroupRefs = (addonGroupRefs) => {
    const resolved = resolveAddonGroupsToItems(addonGroupRefs, addongroupsFromApi);
    if (resolved.length > 0) return JSON.stringify(resolved);
    if (addonGroupRefs && addonGroupRefs.length > 0) return JSON.stringify(createPetpoojaAddOns(addonGroupRefs));
    return null;
  };

  return petpoojaData.items.map(item => {
    // Use item_price from API for display (e.g. 440.00); fallback to price if not present
    const itemPrice = parseFloat(item.item_price || item.price || 0);
    return {
    id: item.itemid,
    item_name: item.itemname,
    base_price: itemPrice,
    item_price: itemPrice,
    discount_price: parseFloat(item.markup_price || item.item_price || item.price || 0),
    description: item.itemdescription || '',
    images: item.item_image_url ? [item.item_image_url] : [],
    rating: 0, // Petpooja doesn't provide rating in this response
    updated_rating: 0, // Petpooja doesn't provide rating count in this response
    type: item.item_attributeid === '1' ? 'veg' : 'non veg', // 1 = veg, 2 = non-veg
    in_stock: parseInt(item.in_stock) === 2 ? 1 : 0, // 2 = in stock, 1 = out of stock
    variation: item.variation ? item.variation.map(variation => {
      const variationItemPrice = parseFloat(variation.item_price || variation.price || 0);
      const variationPrice = parseFloat(variation.price || variation.item_price || 0);
      return {
      name: variation.name,
      price: variationItemPrice,
      item_price: variationItemPrice,
      discount_price: parseFloat(variation.markup_price || variation.item_price || variation.price || 0),
      groupname: variation.groupname,
      addon: variation.addon || [],
      variation_item_discount: variation.variation_item_discount ?? null,
      variation_item_discount_type: variation.variation_item_discount_type ?? null,
      // Include add-ons for this variation (resolve from addongroups so Onion, Minus, Toppings show)
      add_ons: variation.addon && variation.addon.length > 0 ? getAddonsForGroupRefs(variation.addon) : null
    };
    }) : [],
    add_ons: item.addon && item.addon.length > 0 ? getAddonsForGroupRefs(item.addon) : null,
    recommend: parseInt(item.is_recommend) === 1 ? 1 : 0,
    status: item.active === '1' ? 'active' : 'inactive',
    // CRITICAL FIX: Use restaurant ID from /restaurant-detail-for-web API instead of Petpooja ID
    restaurant_id_new: restaurantIdFromDetail || null,
    restaurant_id: restaurantIdFromDetail || null, // Also set restaurant_id for consistency
    like: parseInt(item.item_favorite) === 1 ? 1 : 0,
    slug: item.itemid, // Using itemid as slug
    menu_category_id: item.item_categoryid,
    tax: item.item_tax || null,
    option: [],
    // Additional Petpooja specific fields
    item_ordertype: item.item_ordertype,
    item_packingcharges: item.item_packingcharges,
    minimumpreparationtime: item.minimumpreparationtime,
    cuisine: item.cuisine || [],
    item_tags: item.item_tags || [],
    spice_level: item.item_info?.spice_level || 'not-applicable',
    // Pass through discount fields so calculateDiscountedPrice works for PetPooja items
    menu_item_discount: item.menu_item_discount ?? null,
    menu_item_discount_type: item.menu_item_discount_type ?? null
  };
  });
};

// Transform Petpooja categories to match your existing structure
export const transformPetpoojaCategories = (petpoojaData) => {
  if (!petpoojaData || !petpoojaData.categories || !Array.isArray(petpoojaData.categories)) {
    return [];
  }

  // Count items per category
  const itemCounts = {};
  if (petpoojaData.items && Array.isArray(petpoojaData.items)) {
    petpoojaData.items.forEach(item => {
      const categoryId = item.item_categoryid;
      itemCounts[categoryId] = (itemCounts[categoryId] || 0) + 1;
    });
  }

  return petpoojaData.categories.map(category => ({
    id: category.categoryid,
    title: category.categoryname,
    total_item: itemCounts[category.categoryid] || 0
  }));
};

/**
 * Calculate restaurant_total_item_price for Petpooja integrated restaurants.
 * Uses restaurant prices (price) from Petpooja API, not app prices (item_price).
 * For items: use item.price; for selected variation: use variation.price; for addons: use addon.price when available.
 * @param {Array} cart - Cart items (each has id, quantity, variation, selectedAddOns/currentAddons)
 * @param {Array} petpoojaItems - Raw items from Petpooja API (details.items): each has itemid, price, item_price, variation[], addon[]
 * @returns {number} Sum of restaurant prices × quantities + addon restaurant prices
 */
export const calculatePetpoojaRestaurantTotalItemPrice = (cart, petpoojaItems) => {
  if (!cart || !Array.isArray(cart) || cart.length === 0) {
    return 0;
  }
  if (!petpoojaItems || !Array.isArray(petpoojaItems)) {
    return 0;
  }

  const itemsById = {};
  petpoojaItems.forEach((item) => {
    const id = String(item.itemid ?? item.id ?? '');
    if (id) itemsById[id] = item;
  });

  let total = 0;
  for (const cartItem of cart) {
    const itemId = String(cartItem.id ?? cartItem.slug ?? '');
    const petpoojaItem = itemsById[itemId];
    if (!petpoojaItem) continue;

    const quantity = parseInt(cartItem.quantity || 1, 10) || 1;
    const variation = cartItem.variation || {};
    const hasVariation = variation && Object.keys(variation).length > 0 && variation.name;

    let unitPrice = 0;
    let addonSource = petpoojaItem.addon || [];
    if (hasVariation && petpoojaItem.variation && Array.isArray(petpoojaItem.variation)) {
      const selectedVar = petpoojaItem.variation.find(
        (v) => String(v.name || '').trim() === String(variation.name || '').trim()
      );
      if (selectedVar) {
        unitPrice = parseFloat(selectedVar.price ?? selectedVar.item_price ?? 0) || 0;
        addonSource = selectedVar.addon || addonSource;
      } else {
        unitPrice = parseFloat(petpoojaItem.price ?? petpoojaItem.item_price ?? 0) || 0;
      }
    } else {
      unitPrice = parseFloat(petpoojaItem.price ?? petpoojaItem.item_price ?? 0) || 0;
    }

    total += unitPrice * quantity;

    const addons = cartItem.selectedAddOns || cartItem.currentAddons || [];
    for (const addon of addons) {
      const q = parseInt(addon.quantity ?? 1, 10) || 1;
      // Use addons_discount_price for restaurant total when available
      const cartAddonPrice = parseFloat(addon.addons_discount_price ?? addon.addons_price ?? addon.price ?? addon.addon_price ?? 0) || 0;
      if (!Array.isArray(addonSource) || addonSource.length === 0) {
        total += cartAddonPrice * q;
        continue;
      }
      const addonId = addon.id ?? addon.addon_id ?? addon.addons_id;
      const addonName = (addon.name ?? addon.addons_name ?? '').trim().toLowerCase();
      const matched = addonSource.find((a) => {
        const aid = String(a.addonid ?? a.addon_id ?? a.id ?? '');
        const aname = String(a.name ?? a.addonname ?? '').trim().toLowerCase();
        if (addonId && aid && String(addonId) === aid) return true;
        if (addonName && aname && addonName === aname) return true;
        return false;
      });
      const addonPrice = matched
        ? parseFloat(matched.price ?? matched.item_price ?? 0) || 0
        : cartAddonPrice;
      total += addonPrice * q;
    }
  }
  return Math.round(total * 100) / 100;
};

/**
 * Flatten raw PetPooja addon source (item or variation addon) into order-payload shape.
 * Handles both group structure (addon_item array) and flat addon array.
 * @param {Array} addonSource - petpoojaItem.addon or selectedVariation.addon from raw API
 * @returns {Array} Addons in payload shape: { addonitemid, addongroup_name, addongroupid, addons_name, addons_discount_price, addons_price, addons_item_instock, addons_food_type, active }
 */
/**
 * @param {Array} addonSource - Item/variation addon array (may be group refs with addon_group_id only, or items with addon_item)
 * @param {Array} [addongroupsFromApi] - Optional details.addongroups from API to resolve group refs to addon items
 */
export const getPetpoojaAddonsInPayloadShape = (addonSource, addongroupsFromApi = []) => {
  if (!addonSource || !Array.isArray(addonSource)) return [];
  const out = [];
  const hasAddongroups = addongroupsFromApi && Array.isArray(addongroupsFromApi) && addongroupsFromApi.length > 0;

  addonSource.forEach((groupOrItem) => {
    const groupId = groupOrItem.addon_group_id ?? groupOrItem.addongroupid ?? groupOrItem.addon_groupid ?? '';
    const groupName = groupOrItem.addon_group_name ?? groupOrItem.addongroup_name ?? groupOrItem.addon_groupname ?? '';
    let items = groupOrItem.addon_item ?? groupOrItem.addon_item_list ?? (groupOrItem.addon_item_id != null || groupOrItem.addonitemid != null || groupOrItem.addonid != null || (groupOrItem.addons_name ?? groupOrItem.addon_name ?? groupOrItem.name) != null ? [groupOrItem] : []);

    if ((!Array.isArray(items) || items.length === 0) && hasAddongroups && groupId) {
      const group = addongroupsFromApi.find((g) => String(g.addongroupid ?? g.addon_group_id ?? '').trim() === String(groupId).trim());
      if (group && group.addongroupitems && Array.isArray(group.addongroupitems)) {
        items = group.addongroupitems;
        const gId = String(group.addongroupid ?? group.addon_group_id ?? groupId);
        const gName = String(group.addongroup_name ?? group.addon_groupname ?? groupName);
        items.forEach((addon) => {
          const addonItemId = addon.addonitemid ?? addon.addon_item_id ?? addon.id ?? '';
          const name = addon.addonitem_name ?? addon.addonitemname ?? addon.addon_name ?? addon.name ?? '';
          const price = parseFloat(addon.addonitem_price ?? addon.item_price ?? 0) || 0;
          const attr = String(addon.attributes ?? '').trim();
          const foodType = attr === '1' ? 'veg' : attr === '2' ? 'non-veg' : 'veg';
          out.push({
            addonitemid: String(addonItemId),
            addongroup_name: gName || 'Starters',
            addongroupid: gId,
            addons_name: String(name || 'Add-on'),
            addons_discount_price: Number(price).toFixed(2),
            addons_price: Number(price).toFixed(2),
            addons_item_instock: addon.addons_item_instock ?? addon.in_stock ?? null,
            addons_food_type: foodType,
            active: addon.active ?? '1'
          });
        });
      }
      return;
    }
    if (!Array.isArray(items) || items.length === 0) return;
    items.forEach((addon) => {
      const addonItemId = addon.addon_item_id ?? addon.addonitemid ?? addon.addon_itemid ?? addon.addonid ?? addon.addon_id ?? addon.id ?? '';
      const name = addon.addon_name ?? addon.addons_name ?? addon.name ?? addon.addonname ?? '';
      const price = parseFloat(addon.price ?? addon.item_price ?? addon.addons_price ?? 0) || 0;
      const discountPrice = parseFloat(addon.discount_price ?? addon.markup_price ?? addon.addons_discount_price ?? addon.addons_price ?? price) || 0;
      out.push({
        addonitemid: String(addonItemId),
        addongroup_name: String((groupName || addon.addongroup_name) ?? (addon.group_name ?? '')),
        addongroupid: String((groupId || addon.addongroupid) ?? (addon.addon_group_id ?? (addon.group_id ?? ''))),
        addons_name: String(name),
        addons_discount_price: Number(discountPrice).toFixed(2),
        addons_price: Number(price).toFixed(2),
        addons_item_instock: addon.addons_item_instock ?? addon.in_stock ?? null,
        addons_food_type: addon.addons_food_type ?? addon.food_type ?? addon.item_attributeid === '1' ? 'veg' : 'non-veg',
        active: addon.active ?? '1'
      });
    });
  });
  return out;
};

/**
 * Get the raw variation object from PetPooja item that matches the selected variation name.
 * Used to read correct variationid and id for payload (not item id).
 * @param {Object} petpoojaItem - Raw item from PetPooja menu API
 * @param {string} selectedVariationName - Selected variation name
 * @returns {Object|null} Raw variation object or null
 */
export const getPetpoojaSelectedRawVariation = (petpoojaItem, selectedVariationName) => {
  if (!petpoojaItem || !petpoojaItem.variation || !Array.isArray(petpoojaItem.variation)) return null;
  const name = String(selectedVariationName || '').trim();
  if (!name) return null;
  return petpoojaItem.variation.find((v) => String(v.name || '').trim() === name) || null;
};

/**
 * Build allVariation array in order-payload shape from raw PetPooja item (no base item, no addon/add_ons).
 * Each entry: name, variation_item_discount_by, discount_price, final_price, price, variation_item_instock, variation_item_discount, variation_item_discount_type, variationid, id, active.
 * @param {Object} petpoojaItem - Raw item from PetPooja menu API
 * @returns {Array} allVariation in payload shape
 */
export const buildAllVariationFromPetpoojaItem = (petpoojaItem) => {
  if (!petpoojaItem || !petpoojaItem.variation || !Array.isArray(petpoojaItem.variation)) return [];
  const toStr = (v) => (v != null ? (typeof v === 'number' ? String(v) : String(v)) : null);
  const priceStr = (v) => (v != null ? (typeof v === 'number' ? Number(v).toFixed(2) : String(v)) : null);
  return petpoojaItem.variation.map((v) => {
    const price = parseFloat(v.price ?? v.item_price ?? 0) || 0;
    const discountPrice = parseFloat(v.markup_price ?? v.discount_price ?? v.price ?? v.item_price ?? 0) || 0;
    const discount = v.variation_item_discount ?? null;
    const discountType = v.variation_item_discount_type ?? null;
    const finalPrice = discount && discountType ? (discountType.toLowerCase() === 'percentage' ? price - (price * parseFloat(discount) / 100) : price - parseFloat(discount)) : discountPrice;
    return {
      name: v.name || '',
      variation_item_discount_by: v.variation_item_discount_by ?? null,
      discount_price: priceStr(v.discount_price ?? v.markup_price ?? v.price ?? v.item_price) || priceStr(price),
      final_price: typeof finalPrice === 'number' && !isNaN(finalPrice) ? Number(finalPrice).toFixed(2) : priceStr(discountPrice),
      price: priceStr(v.price ?? v.item_price) || priceStr(price),
      variation_item_instock: v.variation_item_instock ?? null,
      variation_item_discount: discount,
      variation_item_discount_type: discountType,
      variationid: toStr(v.variationid ?? v.variation_id ?? v.id) || '',
      id: toStr(v.id ?? v.variation_item_id ?? v.variationid) || '',
      active: v.active ?? '1'
    };
  });
};

/**
 * Get all addons for a PetPooja item in order-payload shape (for menu_detail.allAddons).
 * Uses selected variation's addon list if available, else item-level addon.
 * When item/variation only has addon_group_id refs, pass addongroupsFromApi (details.addongroups) to resolve to actual items.
 * @param {Object} petpoojaItem - Raw item from PetPooja menu API
 * @param {string} selectedVariationName - Selected variation name (optional)
 * @param {Array} [addongroupsFromApi] - Optional details.addongroups to resolve group refs to addon items
 * @returns {Array} allAddons in payload shape
 */
export const getAllAddonsForPetpoojaItem = (petpoojaItem, selectedVariationName, addongroupsFromApi = []) => {
  if (!petpoojaItem) return [];
  let addonSource = petpoojaItem.addon || [];
  if (selectedVariationName && petpoojaItem.variation && Array.isArray(petpoojaItem.variation)) {
    const selectedVar = petpoojaItem.variation.find(
      (v) => String(v.name || '').trim() === String(selectedVariationName || '').trim()
    );
    if (selectedVar && selectedVar.addon && selectedVar.addon.length > 0) {
      addonSource = selectedVar.addon;
    }
  }
  return getPetpoojaAddonsInPayloadShape(addonSource, addongroupsFromApi);
};

/**
 * Normalize a single addon (from cart selectedAddOns/currentAddons) to order-payload shape.
 * If rawAddonsList (from getPetpoojaAddonsInPayloadShape) is provided, match by name/id to fill addonitemid, addongroupid, addongroup_name.
 * @param {Object} addon - Cart addon: { name, addons_name, addons_price, addons_discount_price, type, addonitemid, ... }
 * @param {Array} rawAddonsList - Optional list in payload shape to match for ids
 * @returns {Object} Addon in payload shape
 */
export const normalizeAddonToPayloadShape = (addon, rawAddonsList = []) => {
  const name = String(addon.addons_name ?? addon.name ?? '').trim();
  const nameLower = name.toLowerCase();
  const matched = rawAddonsList.length
    ? rawAddonsList.find(
        (r) =>
          (r.addonitemid && String(addon.addonitemid ?? addon.addon_id ?? addon.id ?? '') === r.addonitemid) ||
          (r.addons_name && String(r.addons_name).trim().toLowerCase() === nameLower)
      )
    : null;
  const price = parseFloat(addon.addons_price ?? addon.price ?? addon.addon_price ?? 0) || 0;
  const discountPrice = parseFloat(addon.addons_discount_price ?? addon.addons_price ?? addon.price ?? addon.addon_price ?? 0) || 0;
  return {
    addonitemid: matched ? matched.addonitemid : String(addon.addonitemid ?? addon.addon_id ?? addon.id ?? ''),
    addongroup_name: matched ? matched.addongroup_name : String(addon.addongroup_name ?? addon.group_name ?? ''),
    addongroupid: matched ? matched.addongroupid : String(addon.addongroupid ?? addon.addon_group_id ?? addon.group_id ?? ''),
    addons_name: name || matched?.addons_name || 'Add-on',
    addons_discount_price: Number(discountPrice).toFixed(2),
    addons_price: Number(price).toFixed(2),
    addons_item_instock: addon.addons_item_instock ?? matched?.addons_item_instock ?? null,
    addons_food_type: addon.addons_food_type ?? addon.type ?? matched?.addons_food_type ?? 'veg',
    active: addon.active ?? matched?.active ?? '1'
  };
};